import React, { useState } from "react";
import { FiChevronDown, FiChevronUp, FiExternalLink } from "react-icons/fi";
import { ImageCarousel } from "newui/features/Company/CompanyProfile/Preview/components/ImageCarousel";
import { formatNumber } from "utils/helpers";
import { PROFILE_TYPE_CONSULTANT } from "utils/constants";
import AccordionLargeTextDisplay from "newui/components/AccordionLargeTextDisplay";
import SectorPill from "newui/components/PillDisplay/SectorPill";

interface ProjectSector {
	id: number;
	name: string;
}

interface ProjectDiscipline {
	id: number;
	name: string;
	expertise?: { id: number; name: string };
}

interface ProcurementMethod {
	id: number;
	name: string;
}

interface ProjectTrade {
	id: number;
	name: string;
}

interface ProjectLocation {
	lat?: number;
	lng?: number;
	value?: string;
}

export interface ViewProjectDetailData {
	name: string;
	additional_notes?: string;
	awards?: string;
	client_name?: string;
	company_role?: string | null;
	disciplines?: ProjectDiscipline[];
	funding_type?: string;
	gross_floor_area?: string | null;
	images?: string[];
	innovation_value_summary?: string;
	location?: ProjectLocation | string | null;
	procurement_methods?: ProcurementMethod[];
	sectors?: ProjectSector[];
	site_area?: string | null;
	summary?: string;
	testimonials?: string;
	trade_value?: string | null;
	trades?: ProjectTrade[];
	value?: number;
	value_humanized?: string;
	year_completed?: string;
}

interface ViewProjectDetailProps {
	profileTypeId?: number;
	project: ViewProjectDetailData;
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<span className="text-sm text-gray-500">{children}</span>
);

const SectionValue: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<span className="text-sm font-medium text-gray-900">{children}</span>
);

const getLocationString = (location: ProjectLocation | string | null | undefined): string => {
	if (!location) return "";
	if (typeof location === "string") return location;
	return location.value ?? "";
};

export const ViewProjectDetail: React.FC<ViewProjectDetailProps> = ({
	project,
	profileTypeId = 2,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const isConsultant = profileTypeId === PROFILE_TYPE_CONSULTANT;
	const isTrade = profileTypeId === 3;
	const locationStr = getLocationString(project.location);

	const hasExpandableContent = !!(
		project.innovation_value_summary ||
		project.awards ||
		project.testimonials ||
		project.additional_notes
	);

	return (
		<div className="flex flex-col gap-6 pb-8 pt-4">
			{/* Image Carousel */}
			{project.images && project.images.length > 0 && (
				<ImageCarousel
					images={project.images}
					alt={project.name}
					heading={project.name}
					subheading={locationStr}
					height="h-64"
				/>
			)}

			{/* Disciplines / Project Scope (consultant only) */}
			{isConsultant && project.disciplines && project.disciplines.length > 0 && (
				<div>
					<h4 className="mb-2 text-sm font-semibold text-gray-700">
						Project Scope
					</h4>
					<div className="flex flex-wrap gap-2">
						{project.disciplines.map((d) => (
							<span
								key={d.id}
								className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
							>
								{d.name}
							</span>
						))}
					</div>
				</div>
			)}

			{/* Summary */}
			{project.summary && (
				<AccordionLargeTextDisplay title="Summary" text={project.summary} isLoading={false} />
			)}

			{/* Sectors */}
			{project.sectors && project.sectors.length > 0 && (
				<div>
					<h4 className="mb-2 text-sm font-semibold text-gray-700">
						Sectors
					</h4>
					<div className="flex flex-wrap gap-2">
						{project.sectors.map((s) => (
							<SectorPill key={s.id} name={s.name} />
						))}
					</div>
				</div>
			)}

			{/* Key Figures */}
			<div className="flex flex-col gap-3">
				{isTrade ? (
					<div className="flex justify-between border-b border-gray-100 pb-2">
						<SectionLabel>Trade Packages Value (AUD)</SectionLabel>
						<SectionValue>
							{project.trade_value === null
								? "Confidential/NA"
								: `$${project.trade_value}`}
						</SectionValue>
					</div>
				) : (
					<div className="flex justify-between border-b border-gray-100 pb-2">
						<SectionLabel>Project Construction Value (AUD)</SectionLabel>
						<SectionValue>
							{project.value === 0
								? "Confidential/NA"
								: `$${project.value_humanized ?? project.value}`}
						</SectionValue>
					</div>
				)}

				{project.year_completed && (
					<div className="flex justify-between border-b border-gray-100 pb-2">
						<SectionLabel>Year Complete</SectionLabel>
						<SectionValue>{project.year_completed}</SectionValue>
					</div>
				)}

				{locationStr && (
					<div className="flex justify-between border-b border-gray-100 pb-2">
						<SectionLabel>Location</SectionLabel>
						<SectionValue>{locationStr}</SectionValue>
					</div>
				)}

				<div className="flex justify-between border-b border-gray-100 pb-2">
					<SectionLabel>
						{isTrade ? "Client Name" : "Principal/Owner of Project"}
					</SectionLabel>
					<SectionValue>
						{project.client_name || "—"}
					</SectionValue>
				</div>

				{isTrade && project.trades && (
					<div className="flex justify-between border-b border-gray-100 pb-2">
						<SectionLabel>Trade Package/s</SectionLabel>
						<SectionValue>
							{project.trades.length > 0
								? project.trades.map((t) => t.name).join(", ")
								: "Confidential/NA"}
						</SectionValue>
					</div>
				)}

				{!isTrade &&
					project.procurement_methods &&
					project.procurement_methods.length > 0 && (
						<div className="flex justify-between border-b border-gray-100 pb-2">
							<SectionLabel>Procurement Model</SectionLabel>
							<SectionValue>
								{project.procurement_methods
									.map((m) => m.name)
									.join(", ")}
							</SectionValue>
						</div>
					)}

				{!isTrade && project.funding_type && (
					<div className="flex justify-between border-b border-gray-100 pb-2">
						<SectionLabel>Type of Funding</SectionLabel>
						<SectionValue>{project.funding_type}</SectionValue>
					</div>
				)}

				{!isTrade && project.site_area && (
					<div className="flex justify-between border-b border-gray-100 pb-2">
						<SectionLabel>Site Area (Ha)</SectionLabel>
						<SectionValue>{project.site_area}</SectionValue>
					</div>
				)}

				{!isTrade && project.gross_floor_area && (
					<div className="flex justify-between border-b border-gray-100 pb-2">
						<SectionLabel>Gross Floor Area (m²)</SectionLabel>
						<SectionValue>
							{formatNumber(project.gross_floor_area)}
						</SectionValue>
					</div>
				)}
			</div>

			{/* Expandable Content */}
			{!isTrade && hasExpandableContent && (
				<div>
					<button
						type="button"
						onClick={() => setIsExpanded(!isExpanded)}
						className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
					>
						{isExpanded ? "Show Less" : "Show More"}
						{isExpanded ? (
							<FiChevronUp size={16} />
						) : (
							<FiChevronDown size={16} />
						)}
					</button>

					{isExpanded && (
						<div className="mt-4 flex flex-col gap-4">
							{project.innovation_value_summary && (
								<div>
									<h4 className="mb-1 text-sm font-semibold text-gray-700">
										Innovation / Value Summary
									</h4>
									<div
										className="prose prose-sm max-w-none text-gray-600"
										dangerouslySetInnerHTML={{
											__html: project.innovation_value_summary,
										}}
									/>
								</div>
							)}

							{project.awards && (
								<div>
									<h4 className="mb-1 text-sm font-semibold text-gray-700">
										Awards
									</h4>
									<p className="text-sm text-gray-600">
										{project.awards}
									</p>
								</div>
							)}

							{project.testimonials && (
								<a
									href={project.testimonials}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-2 rounded border border-blue-200 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50"
								>
									<FiExternalLink size={14} />
									View Client Testimonial
								</a>
							)}

							{project.additional_notes && (
								<div>
									<h4 className="mb-1 text-sm font-semibold text-gray-700">
										Additional Notes
									</h4>
									<div
										className="prose prose-sm max-w-none text-gray-600"
										dangerouslySetInnerHTML={{
											__html: project.additional_notes,
										}}
									/>
								</div>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
};
